import React, { useMemo, useState } from 'react';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { useStacklineTheme } from '@stackline/ui';
import { useAppSelector } from 'src/utils/Hooks';
import Box from '@mui/material/Box';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BusinessUnitsCreationModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/BusinessUnitsCreation/BusinessUnitsCreationModal';
import { NoDataPlaceholderWithChildren } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/NoDataPlaceholder';
import { CreateNewSegmentLink } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsViewTab';
import { EMPTY_BUSINESS_UNIT_NAME } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/utils';
import AtlasV2BusinessUnitTable from 'src/components/AtlasV2Components/AtlasV2LayoutComponents/AtlasV2HomeComponents/AtlasV2BusinessUnitTable';
import { RightArrow } from 'src/components/SvgIcons';

const CreateBusinessUnitLink = ({
  text = 'Create a business unit',
  sx = {},
  onClick
}: {
  text?: string;
  fontWeight?: number;
  sx?: React.CSSProperties;
  onClick?: () => void;
}) => {
  return (
    <Text
      sx={{
        ...{
          fontWeight: 500,
          ':hover': {
            cursor: 'pointer',
            textDecoration: 'underline'
          }
        },
        ...sx
      }}
      onClick={onClick}
    >
      {text}
      <RightArrow
        style={{
          width: '12px',
          height: '12px',
          marginLeft: '4px',
          position: 'relative',
          top: '2px'
        }}
      />
    </Text>
  );
};

const NoDataPlaceholder = ({
  setBusinessUnitCreationModalOpen
}: {
  setBusinessUnitCreationModalOpen: (open: boolean) => void;
}) => {
  const { mySegments, teamSegments } = useAppSelector((state) => state.segments);

  return (
    <Box sx={{ margin: '80px auto 0px' }}>
      <NoDataPlaceholderWithChildren
        title="Add business unit"
        subtitle="There are currently no business units to display. All of your business units will be available on this page."
      >
        {mySegments.length > 0 || teamSegments.length > 0 ? (
          <CreateBusinessUnitLink onClick={() => setBusinessUnitCreationModalOpen(true)} />
        ) : (
          <CreateNewSegmentLink />
        )}
      </NoDataPlaceholderWithChildren>
    </Box>
  );
};

/**
 * Business units tab on the segments page
 */
const AtlasV2BusinessUnitsTab = () => {
  const [value, setValue] = useState('');
  const businessUnits = useAppSelector((state) => state.segments.businessUnits);
  const [businessUnitCreationModalOpen, setBusinessUnitCreationModalOpen] = useState(false);
  const theme = useStacklineTheme();

  const filteredBusinessUnits = useMemo(() => {
    return businessUnits.filter((unit) =>
      (unit.displayName || EMPTY_BUSINESS_UNIT_NAME).toLowerCase().includes(value.toLowerCase())
    );
  }, [businessUnits, value]);

  return (
    <Flex flexDirection="column">
      <SearchTextField
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Search business units"
      />
      {filteredBusinessUnits.length < 1 && (
        <NoDataPlaceholder setBusinessUnitCreationModalOpen={setBusinessUnitCreationModalOpen} />
      )}
      {filteredBusinessUnits.length > 0 && (
        <Box marginTop={theme.spacing.lg}>
          <Flex flexDirection="column" gap={104}>
            {[...filteredBusinessUnits]
              .sort((a, b) => b.children.length - a.children.length) // Show units with most segments first
              .map((businessUnit) => (
                <AtlasV2BusinessUnitTable key={businessUnit.id} businessUnit={businessUnit} />
              ))}
          </Flex>
        </Box>
      )}
      <BusinessUnitsCreationModal
        handleClose={() => {
          setBusinessUnitCreationModalOpen(false);
        }}
        open={businessUnitCreationModalOpen}
      />
    </Flex>
  );
};

export default AtlasV2BusinessUnitsTab;
